// activeSamplingEvent/activeSamplingEventActions.js

import { getSamplingEventById } from "../../api/SamplingEventsApi";
import { SamplingEvent } from "../../models/SamplingEvent/SamplingEvent";
import { activeSamplingEventInitialState } from "./activeSamplingEventState";
import { makeSamplingEventFormActions } from "../forms/samplingEventForm/samplingEventFormActions";

export const makeActiveSamplingEventActions = (set, get) => ({
  fetchActiveSamplingEvent: async (eventId) => {
    set((s) => ({
      activeSamplingEvent: {
        ...s.activeSamplingEvent,
        isLoading: true,
        error: null,
      },
    }));

    try {
      const response = await getSamplingEventById(eventId);
      const rawData = response?.data || response;

      if (rawData && rawData.sampling_event) {
        const samplingEventInstance = new SamplingEvent(rawData.sampling_event);
        const allProjects = get().projects.list || [];
        samplingEventInstance.fromExistingSamplingEvent(allProjects);

        set((s) => ({
          activeSamplingEvent: {
            ...s.activeSamplingEvent,
            isLoading: false,
            currentEvent: samplingEventInstance,
            _rev: 0,
          },
        }));
        return { ok: true };
      }

      throw new Error("Invalid event data received from server.");
    } catch (error) {
      const message = error?.message || "Failed to load sampling event";
      set((s) => ({
        activeSamplingEvent: {
          ...s.activeSamplingEvent,
          isLoading: false,
          error: message,
          currentEvent: null,
        },
      }));
      try {
        get().notificationActions?.showNotification?.({
          type: "error",
          title: "Error Loading Sampling Event",
          message,
        });
      } catch {}
      return { ok: false, error };
    }
  },

  notifySuccess: (title, message) => {
    try {
      get().notificationActions?.showNotification?.({
        type: "success",
        title,
        message,
      });
    } catch {}
  },

  setCurrentTab: (tab) =>
    set((s) => ({
      activeSamplingEvent: { ...s.activeSamplingEvent, currentTab: tab },
    })),

  /**
   * Apply a websocket patch by delegating to samplingEventFormActions.handleBroadcast.
   * This routes broadcasts to the appropriate section action file which then
   * mutates the class instance directly using setter methods.
   */
  applyWebsocketPatch: (patchData) => {
    const current = get().activeSamplingEvent.currentEvent;
    if (!current) return;

    // Special case: hydrate replaces everything
    if (patchData.patch_type === "hydrate_event") {
      const hydrated = new SamplingEvent(patchData.patch);
      const allProjects = get().projects.list || [];
      hydrated.fromExistingSamplingEvent(allProjects);
      // Preserve UI state
      hydrated.activeComponentTabs = current.activeComponentTabs;
      hydrated.jsaUpdatesToMonitor = current.jsaUpdatesToMonitor;

      set((s) => ({
        activeSamplingEvent: {
          ...s.activeSamplingEvent,
          currentEvent: hydrated,
          _rev: (s.activeSamplingEvent._rev || 0) + 1,
        },
      }));
      return;
    }

    // Delegate all other patches to the nested form broadcast router
    // (mirrors activeDelDraftsActions.delFormActions pattern)
    const formActions =
      get().activeSamplingEventActions?.samplingEventFormActions;
    if (formActions?.handleBroadcast) {
      formActions.handleBroadcast(patchData);
    } else {
      console.warn(
        "activeSamplingEventActions.samplingEventFormActions.handleBroadcast not available"
      );
    }

    // Trigger React re-render by bumping revision
    set((s) => ({
      activeSamplingEvent: {
        ...s.activeSamplingEvent,
        _rev: (s.activeSamplingEvent._rev || 0) + 1,
      },
    }));
  },

  /**
   * Apply a JSA-specific websocket patch from sampling_event_jsa_update event.
   * Routes to jsaForms.handleJsaBroadcast which handles individual JSA field patches.
   */
  applyJsaWebsocketPatch: (patchData) => {
    const current = get().activeSamplingEvent.currentEvent;
    if (!current) return;

    // Delegate JSA patches to the nested JSA form broadcast handler
    const formActions =
      get().activeSamplingEventActions?.samplingEventFormActions;
    if (formActions?.jsaForms?.handleJsaBroadcast) {
      formActions.jsaForms.handleJsaBroadcast(patchData);
    } else {
      console.warn(
        "activeSamplingEventActions.samplingEventFormActions.jsaForms.handleJsaBroadcast not available"
      );
    }

    // Trigger React re-render by bumping revision
    set((s) => ({
      activeSamplingEvent: {
        ...s.activeSamplingEvent,
        _rev: (s.activeSamplingEvent._rev || 0) + 1,
      },
    }));
  },

  clearActiveEvent: () =>
    set((s) => ({
      activeSamplingEvent: {
        ...activeSamplingEventInitialState,
      },
    })),

  samplingEventFormActions: Object.freeze(
    makeSamplingEventFormActions(set, get)
  ),
});
